import React, { useEffect, useMemo, useState } from 'react'
import { supabase, SUPABASE_URL } from './supabaseClient'
import { useAuth } from './AuthContext'
import {
  Send, Users, Check, ShieldOff, RefreshCw, AlertCircle, CheckCircle,
  Clock, Mail, Eye,
} from 'lucide-react'

import { C } from './theme'

const fmtDataHora = (d) => d
  ? new Date(d).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—'

// Selecções rápidas de destinatários
const GRUPOS = [
  { id: 'todos',     label: 'Todos os activos',      filtro: (c) => true },
  { id: 'premium',   label: 'Premium (com desconto)', filtro: (c) => Number(c.desconto_percentagem || 0) > 0 },
  { id: 'standard',  label: 'Standard (sem desconto)', filtro: (c) => Number(c.desconto_percentagem || 0) === 0 },
  { id: 'nenhum',    label: 'Limpar selecção',        filtro: () => false },
]

export default function Comunicacoes() {
  const { isAdmin, perfil } = useAuth()
  const [emailTeste, setEmailTeste] = useState('')
  const [clientes, setClientes] = useState([])
  const [seleccionados, setSeleccionados] = useState(() => new Set())
  const [assunto, setAssunto] = useState('')
  const [corpo, setCorpo] = useState('')
  const [incluirInternos, setIncluirInternos] = useState(false)
  const [loading, setLoading] = useState(true)
  const [enviando, setEnviando] = useState('')       // '' | 'teste' | 'circular'
  const [msg, setMsg] = useState(null)               // { tipo:'ok'|'erro', texto }
  const [confirmar, setConfirmar] = useState(false)
  const [historico, setHistorico] = useState([])
  const [erroHistorico, setErroHistorico] = useState('')

  const carregar = async () => {
    setLoading(true)
    const [perfisRes, histRes, cfgRes] = await Promise.all([
      supabase.from('perfis')
        .select('id, nome, email, role, ativo, estado_conta, desconto_percentagem, cliente_interno')
        .eq('role', 'cliente').order('nome'),
      supabase.from('circulares')
        .select('id, criado_em, criado_por, tipo, assunto, total, enviados, falhados')
        .order('criado_em', { ascending: false }).limit(30),
      supabase.from('config_sistema').select('chave, valor').eq('chave', 'email_teste').maybeSingle(),
    ])
    // Destino do teste: o que estiver em config_sistema.email_teste, senão o email do admin
    setEmailTeste(prev => prev || cfgRes?.data?.valor || perfil?.email || '')
    setClientes((perfisRes.data || []).filter(c => c.email && c.ativo !== false && c.estado_conta !== 'recusado'))
    setHistorico(histRes.data || [])
    // A tabela pode ainda não existir — avisa em vez de falhar em silêncio
    setErroHistorico(histRes.error ? histRes.error.message : '')
    setLoading(false)
  }
  useEffect(() => { if (isAdmin) carregar() }, [isAdmin])

  const elegiveis = useMemo(
    () => clientes.filter(c => incluirInternos || !c.cliente_interno),
    [clientes, incluirInternos]
  )

  // Se um interno estava seleccionado e depois foi excluído, não pode continuar na lista
  const destinatarios = useMemo(
    () => elegiveis.filter(c => seleccionados.has(c.id)),
    [elegiveis, seleccionados]
  )

  const aplicarGrupo = (grupo) => {
    setSeleccionados(new Set(elegiveis.filter(grupo.filtro).map(c => c.id)))
    setConfirmar(false)
  }
  const toggleCliente = (id) => {
    setSeleccionados(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
    setConfirmar(false)
  }

  const chamarFuncao = async (payload) => {
    const { data: sess } = await supabase.auth.getSession()
    const token = sess?.session?.access_token
    if (!token) throw new Error('Sessão expirada — volta a entrar.')
    const res = await fetch(`${SUPABASE_URL}/functions/v1/enviar-circular`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || !json.ok) throw new Error(json?.erro || `Erro ${res.status}`)
    return json
  }

  const enviarTeste = async () => {
    setMsg(null); setEnviando('teste')
    try {
      const r = await chamarFuncao({ modo: 'teste', assunto, corpo, para: emailTeste.trim() })
      // A função devolve ok:true mesmo quando o SMTP falha — o que conta é o enviados/falhados
      if (r.enviados > 0) {
        setMsg({ tipo: 'ok', texto: `Email de teste enviado para ${emailTeste.trim() || 'o teu email'}. Confere a caixa de entrada (e o spam) antes de enviar a sério.` })
      } else {
        setMsg({ tipo: 'erro', texto: `O envio FALHOU para ${emailTeste.trim()}${r.erros?.length ? ` — ${r.erros.join(' · ')}` : ' (sem detalhe do servidor)'}` })
      }
    } catch (e) {
      setMsg({ tipo: 'erro', texto: e.message })
    }
    setEnviando('')
  }

  const enviarCircular = async () => {
    setMsg(null); setEnviando('circular')
    try {
      const r = await chamarFuncao({
        modo: 'circular', assunto, corpo,
        destinatarios: destinatarios.map(c => ({ email: c.email, nome: c.nome })),
      })
      setMsg({
        tipo: r.falhados?.length ? 'erro' : 'ok',
        texto: r.falhados?.length
          ? `Enviados ${r.enviados} de ${r.total}. Falharam: ${r.falhados.join(', ')}${r.erros?.length ? ` — ${r.erros.join(' · ')}` : ''}`
          : `Circular enviada para ${r.enviados} cliente(s).`,
      })
      setConfirmar(false)
      await carregar()
    } catch (e) {
      setMsg({ tipo: 'erro', texto: e.message })
    }
    setEnviando('')
  }

  if (!isAdmin) {
    return (
      <div style={{ padding: '60px 28px', textAlign: 'center', color: C.danger }}>
        <ShieldOff size={48} style={{ margin: '0 auto 12px' }} />
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Acesso restrito</h2>
        <p style={{ margin: '6px 0 0', fontSize: '13px', color: C.textMuted }}>Esta página só está disponível para administradores.</p>
      </div>
    )
  }

  const podeEnviar = assunto.trim() && corpo.trim() && destinatarios.length > 0 && !enviando
  const inputBase = { width: '100%', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '13px', padding: '9px 10px', boxSizing: 'border-box', fontFamily: 'inherit' }

  return (
    <div style={{ padding: '20px 28px', flex: 1, overflowY: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '600', color: C.text, margin: '0 0 3px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Send size={20} color={C.primary} /> Comunicações
          </h1>
          <p style={{ fontSize: '12px', color: C.textMuted, margin: 0 }}>
            Circulares aos clientes · envia sempre um teste a ti primeiro · fica tudo registado
          </p>
        </div>
        <button onClick={carregar} disabled={loading}
          style={{ height: '34px', padding: '0 12px', border: `1px solid ${C.border}`, background: C.surface, borderRadius: '4px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <RefreshCw size={12} /> Atualizar
        </button>
      </div>

      {erroHistorico && (
        <div style={{ background: C.warningSoft, border: `1px solid ${C.warning}`, borderRadius: '6px', padding: '10px 14px', marginBottom: '14px', fontSize: '12px', color: '#92400e' }}>
          <strong>Tabela de registo em falta.</strong> Corre o <code>tabela.sql</code> de <code>supabase/functions/enviar-circular/</code> no SQL Editor.
          Sem ela o envio funciona, mas não fica histórico. ({erroHistorico})
        </div>
      )}

      {msg && (
        <div style={{
          background: msg.tipo === 'ok' ? C.successSoft : C.dangerSoft,
          border: `1px solid ${msg.tipo === 'ok' ? C.success : C.danger}`,
          color: msg.tipo === 'ok' ? C.success : C.danger,
          borderRadius: '6px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px', fontWeight: '600',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          {msg.tipo === 'ok' ? <CheckCircle size={15} /> : <AlertCircle size={15} />} {msg.texto}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 340px) 1fr', gap: '16px', alignItems: 'start' }}>

        {/* ─── QUEM RECEBE ─────────────────────────────────────────── */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, background: C.bg, fontSize: '11px', fontWeight: '800', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Users size={12} /> Quem recebe · {destinatarios.length} de {elegiveis.length}
          </div>

          <div style={{ padding: '10px 14px', display: 'flex', flexWrap: 'wrap', gap: '6px', borderBottom: `1px solid ${C.border}` }}>
            {GRUPOS.map(g => (
              <button key={g.id} onClick={() => aplicarGrupo(g)}
                style={{ padding: '4px 10px', border: `1px solid ${C.border}`, background: C.surface, borderRadius: '12px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', color: C.text }}>
                {g.label}
              </button>
            ))}
          </div>

          <label style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: C.textMuted, cursor: 'pointer', borderBottom: `1px solid ${C.border}` }}>
            <input type="checkbox" checked={incluirInternos} onChange={e => { setIncluirInternos(e.target.checked); setConfirmar(false) }} style={{ cursor: 'pointer', accentColor: C.primary }} />
            Incluir clientes internos
          </label>

          <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
            {loading ? (
              <p style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: C.textMuted, margin: 0 }}>A carregar…</p>
            ) : elegiveis.length === 0 ? (
              <p style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: C.textMuted, margin: 0 }}>Sem clientes activos.</p>
            ) : elegiveis.map(c => {
              const on = seleccionados.has(c.id)
              return (
                <label key={c.id}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', background: on ? '#eff6fc' : 'transparent' }}>
                  <input type="checkbox" checked={on} onChange={() => toggleCliente(c.id)} style={{ cursor: 'pointer', accentColor: C.primary }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nome || c.email}</span>
                    <span style={{ display: 'block', fontSize: '10px', color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</span>
                  </span>
                  {Number(c.desconto_percentagem) > 0 && (
                    <span style={{ fontSize: '9px', fontWeight: '800', color: C.success }}>{c.desconto_percentagem}%</span>
                  )}
                  {c.cliente_interno && (
                    <span style={{ fontSize: '9px', fontWeight: '800', color: C.warning }}>INT</span>
                  )}
                </label>
              )
            })}
          </div>
        </div>

        {/* ─── MENSAGEM ────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '14px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '6px' }}>Assunto</label>
            <input value={assunto} onChange={e => { setAssunto(e.target.value); setConfirmar(false) }}
              placeholder="Ex: Actualização de preços a partir de 1 de Agosto"
              style={{ ...inputBase, marginBottom: '14px' }} />

            <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '6px' }}>
              Mensagem <span style={{ fontWeight: '500', textTransform: 'none', letterSpacing: 0 }}>— escreve <code>{'{nome}'}</code> onde quiseres o nome do cliente. Linha em branco separa parágrafos.</span>
            </label>
            <textarea value={corpo} onChange={e => { setCorpo(e.target.value); setConfirmar(false) }} rows={12}
              placeholder={'Olá {nome},\n\nVenho informar que...\n\nCumprimentos,\nLogicPRO'}
              style={{ ...inputBase, resize: 'vertical', lineHeight: '1.6' }} />

            <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, whiteSpace: 'nowrap' }}>Teste para:</label>
                <input type="email" value={emailTeste} onChange={e => setEmailTeste(e.target.value)}
                  placeholder="o.teu@email.pt"
                  title="Para onde vai o email de teste. Não afecta a circular real."
                  style={{ height: '36px', width: '220px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '12px', padding: '0 9px', boxSizing: 'border-box' }} />
              </span>
              <button onClick={enviarTeste} disabled={!assunto.trim() || !corpo.trim() || !!enviando}
                style={{ height: '36px', padding: '0 14px', border: `1px solid ${C.border}`, background: C.surface, borderRadius: '4px', cursor: (!assunto.trim() || !corpo.trim() || enviando) ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px', opacity: (!assunto.trim() || !corpo.trim()) ? 0.5 : 1 }}>
                <Eye size={13} /> {enviando === 'teste' ? 'A enviar…' : 'Enviar teste'}
              </button>

              {!confirmar ? (
                <button onClick={() => setConfirmar(true)} disabled={!podeEnviar}
                  style={{ height: '36px', padding: '0 16px', border: 'none', background: podeEnviar ? C.primary : C.borderStrong, color: '#fff', borderRadius: '4px', cursor: podeEnviar ? 'pointer' : 'not-allowed', fontSize: '12px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Send size={13} /> Enviar a {destinatarios.length} cliente(s)
                </button>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '12px', fontWeight: '800', color: C.danger }}>
                    Confirmas o envio para {destinatarios.length} cliente(s)?
                  </span>
                  <button onClick={enviarCircular} disabled={!!enviando}
                    style={{ height: '36px', padding: '0 16px', border: 'none', background: C.danger, color: '#fff', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '800' }}>
                    {enviando === 'circular' ? 'A enviar…' : 'Sim, enviar'}
                  </button>
                  <button onClick={() => setConfirmar(false)}
                    style={{ height: '36px', padding: '0 12px', border: `1px solid ${C.border}`, background: C.surface, borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                    Cancelar
                  </button>
                </span>
              )}
            </div>
          </div>

          {/* ─── HISTÓRICO ─────────────────────────────────────────── */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, background: C.bg, fontSize: '11px', fontWeight: '800', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Clock size={12} /> Enviados recentemente
            </div>
            {historico.length === 0 ? (
              <p style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: C.textMuted, margin: 0 }}>Ainda não foi enviada nenhuma circular.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <tbody>
                  {historico.map(h => (
                    <tr key={h.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '8px 14px', color: C.textMuted, whiteSpace: 'nowrap' }}>{fmtDataHora(h.criado_em)}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{ fontWeight: '700', color: C.text }}>{h.assunto}</span>
                        {h.tipo === 'exclusividade' && (
                          <span style={{ marginLeft: '6px', padding: '1px 5px', borderRadius: '3px', fontSize: '9px', fontWeight: '800', background: C.successSoft, color: C.success }}>EXCLUSIVIDADE</span>
                        )}
                      </td>
                      <td style={{ padding: '8px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <span style={{ color: h.falhados?.length ? C.danger : C.success, fontWeight: '700' }}>
                          {h.enviados}/{h.total}
                        </span>
                        {h.falhados?.length > 0 && (
                          <span title={`Falharam: ${h.falhados.join(', ')}`} style={{ marginLeft: '6px', color: C.danger, fontSize: '10px' }}>
                            {h.falhados.length} falhou
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
